import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import DOMPurify from "dompurify";

import { tw } from "~/utils";

interface BannerContentProps {
  content: string;
  className?: string;
}

export const BannerContent = ({ content, className }: BannerContentProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight);
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
  }, [content, expanded, checkOverflow]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, checkOverflow]);

  const sanitized = DOMPurify.sanitize(content).replace(/&nbsp;/g, " ");

  return (
    <div
      className={tw(
        "bg-violet80 px-5 py-3 text-center text-sm text-white lg:text-base",
        className,
      )}
    >
      {expanded ? (
        <>
          <span
            className="break-words"
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />{" "}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => setExpanded(false)}
          >
            read less
          </button>
        </>
      ) : (
        <div className="relative">
          <div
            ref={contentRef}
            className="line-clamp-2 break-words"
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
          {isOverflowing && (
            <span className="bg-violet80 absolute bottom-0 right-0">
              ...{" "}
              <button
                type="button"
                className="font-medium underline"
                onClick={() => setExpanded(true)}
              >
                read more
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
